/* Darkens each tab as the next card slides over it.

   The stacking itself is pure CSS sticky; this only reports how far a card has
   been covered, as --hp-cover from 0 to 1, so the pile reads as depth rather
   than as four identical bars. */
(() => {
  /* Wraps every word of the statement so they can fade in one after another.
     Done here rather than in Liquid because the text is rich text: splitting
     the markup on whitespace would tear tags apart, whereas walking the text
     nodes leaves inline images and links intact. */
  const splitWords = (intro) => {
    if (intro.dataset.hpSplit) return;

    const walker = document.createTreeWalker(intro, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    let index = 0;

    nodes.forEach((node) => {
      if (!node.nodeValue.trim()) return;

      const frag = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (!part.trim()) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        const span = document.createElement('span');
        span.className = 'hp__w';
        span.style.setProperty('--hp-wi', index++);
        span.textContent = part;
        frag.appendChild(span);
      });
      node.parentNode.replaceChild(frag, node);
    });

    // Inline images sit in the queue too, so a chilli or a chef's hat does not
    // appear before the words around it.
    intro.querySelectorAll('img').forEach((img) => {
      img.classList.add('hp__w');
      img.style.setProperty('--hp-wi', index++);
    });

    intro.dataset.hpSplit = 'true';
  };

  const setupIntro = (root) => {
    const intro = root.querySelector('[data-hp-intro]');
    if (!intro) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      intro.setAttribute('data-hp-in', '');
      return;
    }

    splitWords(intro);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          intro.setAttribute('data-hp-in', '');
          observer.disconnect();
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(intro);

    // Never leave the words invisible if the observer somehow does not fire.
    setTimeout(() => intro.setAttribute('data-hp-in', ''), 3000);
  };

  const init = (root) => {
    if (root.dataset.hpReady) return;
    root.dataset.hpReady = 'true';

    setupIntro(root);

    const items = Array.from(root.querySelectorAll('[data-hp-item]'));
    const hasDecor = !!root.querySelector('.hp__decor');
    if (items.length < 2 && !hasDecor) return;

    let frame = 0;

    const clamp = (n) => Math.min(1, Math.max(0, n));

    /* The cards stick below the header, so its real height is measured rather
       than assumed — it changes with the logo size and differs on phones. */
    const header = document.querySelector('.fh__wrapper');

    const measureHeader = () => {
      root.style.setProperty('--hp-header', `${header ? header.offsetHeight : 0}px`);
    };

    /* Resolved sticky offsets. Only the editor or a resize can change these, so
       reading them per frame would force a style recalc per card for nothing. */
    let stickTops = [];
    const measureSticky = () => {
      stickTops = items.map((item) => parseFloat(getComputedStyle(item).top) || 0);
    };

    const measure = () => {
      frame = 0;
      const viewport = window.innerHeight || 1;

      // Every read first: interleaving them with the writes below forces a
      // fresh layout per card on every scroll frame.
      const rootTop = hasDecor ? root.getBoundingClientRect().top : 0;
      const boxes = items.map((item) => item.getBoundingClientRect());

      // How far the section has travelled up the screen, for the parallax.
      if (hasDecor) {
        root.style.setProperty('--hp-scroll', `${Math.round(viewport - rootTop)}px`);
      }

      items.forEach((item, i) => {
        const box = boxes[i];

        /* Arrival: 1 while the card is still down the page, 0 once it has
           reached the point where it sticks. */
        const stickTop = stickTops[i];
        const distance = Math.max(1, viewport - stickTop);
        item.style.setProperty('--hp-enter', clamp((box.top - stickTop) / distance).toFixed(3));

        // Coverage: 0 while the next card is still below, 1 once it covers this one.
        const nextTop = boxes[i + 1]?.top;
        if (nextTop === undefined) {
          item.style.setProperty('--hp-cover', '0');
          return;
        }
        const travel = box.height || 1;
        item.style.setProperty('--hp-cover', clamp((box.bottom - nextTop) / travel).toFixed(3));
      });
    };

    /* The page is many screens tall and this section occupies a few of them.
       Off screen there is nothing to report, and the drifting decor does not
       need compositing either — both are gated on the same flag. */
    let onScreen = true;

    const schedule = () => {
      if (frame || !onScreen) return;
      frame = requestAnimationFrame(measure);
    };

    /* Flagged when far away rather than when near, so a failed or slow observer
       leaves the section animating exactly as it did before. */
    new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        root.toggleAttribute('data-hp-far', !onScreen);
        if (onScreen) schedule();
      },
      { rootMargin: '100% 0px' }
    ).observe(root);

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', () => {
      measureHeader();
      measureSticky();
      schedule();
    });

    measureHeader();
    measureSticky();
    measure();
  };

  const scan = (scope) => scope.querySelectorAll('[data-hp]').forEach(init);

  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
